// Implements: F-UIX-018, F-USG-005
/**
 * "Consumo del periodo" of My usage (UIX.4, RPT.4): the person's own generations over the
 * report's day range, one row per currency (money is never added across currencies).
 * Pure presentation; money is formatted here, in the backoffice only (OD-12).
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { moneyText } from "../../../shared/ui/moneyText";
import type { MyConsumptionView } from "../../../types/reports";
import { calendarDayText } from "../model/myUsage";
import styles from "./MyUsage.module.css";

export interface MyUsagePeriodProps {
  readonly consumption: MyConsumptionView;
}

export function MyUsagePeriod({ consumption }: MyUsagePeriodProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.sectionTitle}>
        {t("backoffice.myUsage.period.title")}
      </h2>
      <p className={styles.hint}>
        {t("backoffice.myUsage.period.range", {
          from: calendarDayText(consumption.from),
          to: calendarDayText(consumption.to),
        })}
      </p>
      {consumption.totals.length === 0 ? (
        <p className={styles.hint}>{t("backoffice.myUsage.period.empty")}</p>
      ) : (
        <table className={styles.table} aria-labelledby={headingId}>
          <thead>
            <tr>
              <th scope="col">{t("backoffice.myUsage.column.currency")}</th>
              <th scope="col">{t("backoffice.myUsage.column.delivered")}</th>
              <th scope="col">{t("backoffice.myUsage.column.failed")}</th>
              <th scope="col">{t("backoffice.myUsage.column.cancelled")}</th>
              <th scope="col">{t("backoffice.myUsage.column.cost")}</th>
            </tr>
          </thead>
          <tbody>
            {consumption.totals.map((total) => (
              <tr key={total.currency}>
                <th scope="row">{total.currency}</th>
                <td>{total.delivered}</td>
                <td>{total.failed}</td>
                <td>{total.cancelled}</td>
                <td>{moneyText(total.cost_micros, total.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
